import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguageContext } from '@/contexts/LanguageContext';
import { getLoadedTranslations, loadLanguageTranslations } from '@/localization/translations';
import type { Language, LocalizationHook } from '@/localization/types';
import { DEFAULT_LANGUAGE } from '@/localization/languages';

// Re-export everything for backward compatibility
export type { Language };
export {
  translateStatus,
  translateField,
  translateAppointmentType,
  translatePaymentMethod,
  translateCommunicationChannel,
  translateWishlistCategory,
  translateWishlistSource,
  getFullName,
  formatOrderWithCustomer,
  getTranslation,
} from '@/localization/helpers';

// Base modules must include keys used by persistent app shell components (e.g. sidebar).
const APP_BASE_MODULES = ['common', 'navigation', 'reports', 'settings', 'globalSearch', 'forms'];

const APP_ROUTE_MODULES: Record<string, string[]> = {
  // Dashboard uses appointment summary labels that live in the appointments module.
  '': ['dashboard', 'appointments'],
  leads: ['leads'],
  customers: ['customers'],
  products: ['products'],
  'import-history': ['products'],
  appointments: ['appointments'],
  'appointments-v2': ['appointments'],
  measurements: ['measurements'],
  // Order detail embeds invoices/payments UI, so both modules are required.
  orders: ['orders', 'invoices'],
  invoices: ['invoices'],
  'payment-requests': ['invoices'],
  payments: ['invoices'],
  alterations: ['alterations'],
  forms: ['forms'],
  // Suppliers reuses Orders-style grid control labels for parity.
  suppliers: ['suppliers', 'orders'],
  'purchase-orders': ['suppliers'],
  reports: ['reports'],
  settings: ['settings'],
  notifications: ['settings'],
};

function getAppRouteModuleNames(pathname: string): string[] {
  const modules = new Set<string>(APP_BASE_MODULES);
  const routeWithoutPrefix = pathname.replace(/^\/app\/?/, '');
  const topLevelRoute = routeWithoutPrefix.split('/')[0] ?? '';
  const routeModules = APP_ROUTE_MODULES[topLevelRoute] ?? [];

  routeModules.forEach((moduleName) => modules.add(moduleName));
  return Array.from(modules);
}

function resolveTranslationModules(pathname: string): string[] | undefined {
  if (pathname.startsWith('/app')) {
    return getAppRouteModuleNames(pathname);
  }
  return undefined;
}

export function useLocalization(): LocalizationHook {
  const { language, setLanguage } = useLanguageContext();
  const location = useLocation();
  const [, setRevision] = useState(0);

  const translationModules = useMemo(() => resolveTranslationModules(location.pathname), [location.pathname]);
  const translationModuleKey = translationModules?.join('|') ?? 'all-modules';

  useEffect(() => {
    let active = true;

    const load = async () => {
      const loadOperations: Promise<Record<string, string>>[] = [
        loadLanguageTranslations(language, translationModules),
      ];

      if (language !== DEFAULT_LANGUAGE) {
        loadOperations.push(loadLanguageTranslations(DEFAULT_LANGUAGE, translationModules));
      }

      await Promise.all(loadOperations);
      if (active) setRevision((value) => value + 1);
    };

    void load();
    return () => {
      active = false;
    };
  }, [language, translationModuleKey]);

  const t = (key: string, params?: Record<string, any>): string => {
    let translation =
      getLoadedTranslations(language)?.[key] ||
      getLoadedTranslations(DEFAULT_LANGUAGE)?.[key] ||
      key;

    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        translation = translation.replace(`{${param}}`, String(value));
      });
    }

    return translation;
  };

  return {
    language,
    setLanguage,
    t,
  };
}
